import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  Group,
  Mesh,
  MeshLambertMaterial,
  RepeatWrapping,
  SRGBColorSpace,
} from 'three'
import type { Building, City } from '../world/cityGen'

/**
 * One facade texture tile spans this much wall, in metres — sized so a window
 * is roughly 1.8 m wide and a floor 3.8 m tall. Smaller tiles pack more windows
 * per metre, and at any distance that turns the whole skyline into a moiré
 * hatch rather than a building.
 */
const TILE_W = 7.2
const TILE_H = 7.6
/** Distinct window-phase offsets available per building. */
const WINDOW_PHASES = 4

/**
 * The city, built as **merged** geometry rather than instanced boxes.
 *
 * Instancing is the reflex for hundreds of boxes, but it forces every instance
 * to share one UV set: scaling a unit cube to a 40 m tower stretches the window
 * texture with it, and the skyline ends up with windows of a dozen different
 * sizes. Merging lets each building carry UVs scaled to its real dimensions —
 * uniform windows everywhere — and a few hundred boxes is a handful of
 * kilobytes of vertices. Two draw calls for the whole city either way, so the
 * merge costs nothing and fixes the thing that actually reads as wrong.
 */
export function buildCity(city: City): Group {
  const group = new Group()
  group.name = 'city'

  const facade = new MeshLambertMaterial({
    map: makeFacadeTexture(),
    emissiveMap: makeWindowEmissiveTexture(),
    emissive: new Color(0xffffff),
    // Restrained: lit windows should be points of interest against a dark
    // facade, not the facade itself.
    emissiveIntensity: 0.55,
    vertexColors: true,
  })
  const concrete = new MeshLambertMaterial({
    map: makeRoofTexture(),
    vertexColors: true,
  })

  group.add(new Mesh(buildWalls(city.buildings), facade))
  group.add(new Mesh(buildRoofsAndClutter(city.buildings), concrete))
  return group
}

// ---------------------------------------------------------------------------
// geometry
// ---------------------------------------------------------------------------

/** Scratch accumulators so a build is one pass with no intermediate arrays. */
interface MeshBuilder {
  position: number[]
  normal: number[]
  uv: number[]
  color: number[]
  index: number[]
}

function newBuilder(): MeshBuilder {
  return { position: [], normal: [], uv: [], color: [], index: [] }
}

function toGeometry(b: MeshBuilder): BufferGeometry {
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(b.position), 3))
  geometry.setAttribute('normal', new BufferAttribute(new Float32Array(b.normal), 3))
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(b.uv), 2))
  geometry.setAttribute('color', new BufferAttribute(new Float32Array(b.color), 3))
  geometry.setIndex(b.index)
  geometry.computeBoundingSphere()
  return geometry
}

/**
 * Add a quad. `uvW`/`uvH` are in texture tiles, and `uvOffset` shifts the
 * window grid per building so neighbouring towers do not light up identically.
 */
function quad(
  b: MeshBuilder,
  corners: [number, number, number][],
  normal: [number, number, number],
  uvW: number,
  uvH: number,
  uvOffset: number,
  tint: [number, number, number],
): void {
  const base = b.position.length / 3
  const uvs: [number, number][] = [
    [uvOffset, 0],
    [uvOffset + uvW, 0],
    [uvOffset + uvW, uvH],
    [uvOffset, uvH],
  ]
  for (let i = 0; i < 4; i++) {
    b.position.push(...corners[i])
    b.normal.push(...normal)
    b.uv.push(...uvs[i])
    b.color.push(...tint)
  }
  b.index.push(base, base + 1, base + 2, base, base + 2, base + 3)
}

function buildWalls(buildings: Building[]): BufferGeometry {
  const b = newBuilder()

  for (const building of buildings) {
    const hw = building.width / 2
    const hd = building.depth / 2
    const h = building.height
    const { x, z } = building
    const tint = facadeTint(building.variant)
    // Snap to whole tiles so windows are never sliced mid-pane at a corner.
    const uw = Math.max(1, Math.round(building.width / TILE_W))
    const ud = Math.max(1, Math.round(building.depth / TILE_W))
    const uh = Math.max(1, Math.round(h / TILE_H))
    // Shift by whole windows: a whole-*tile* offset is a no-op (the texture
    // repeats every tile) which is why every tower looked identical.
    const offset = Math.floor(building.variant * WINDOW_PHASES) / WINDOWS_X

    // +Z / -Z / +X / -X, wound counter-clockwise seen from outside.
    quad(
      b,
      [
        [x - hw, 0, z + hd],
        [x + hw, 0, z + hd],
        [x + hw, h, z + hd],
        [x - hw, h, z + hd],
      ],
      [0, 0, 1],
      uw,
      uh,
      offset,
      tint,
    )
    quad(
      b,
      [
        [x + hw, 0, z - hd],
        [x - hw, 0, z - hd],
        [x - hw, h, z - hd],
        [x + hw, h, z - hd],
      ],
      [0, 0, -1],
      uw,
      uh,
      offset + 1 / WINDOWS_X,
      tint,
    )
    quad(
      b,
      [
        [x + hw, 0, z + hd],
        [x + hw, 0, z - hd],
        [x + hw, h, z - hd],
        [x + hw, h, z + hd],
      ],
      [1, 0, 0],
      ud,
      uh,
      offset + 2 / WINDOWS_X,
      tint,
    )
    quad(
      b,
      [
        [x - hw, 0, z - hd],
        [x - hw, 0, z + hd],
        [x - hw, h, z + hd],
        [x - hw, h, z - hd],
      ],
      [-1, 0, 0],
      ud,
      uh,
      offset + 3 / WINDOWS_X,
      tint,
    )
  }

  return toGeometry(b)
}

function buildRoofsAndClutter(buildings: Building[]): BufferGeometry {
  const b = newBuilder()

  for (const building of buildings) {
    const hw = building.width / 2
    const hd = building.depth / 2
    const h = building.height
    const { x, z } = building
    const tint = roofTint(building.variant)

    quad(
      b,
      [
        [x - hw, h, z - hd],
        [x + hw, h, z - hd],
        [x + hw, h, z + hd],
        [x - hw, h, z + hd],
      ],
      [0, 1, 0],
      building.width / 6,
      building.depth / 6,
      0,
      tint,
    )

    // A parapet plus a couple of plant boxes. Rooftops are where the player
    // spends the most time looking, and a bare plane reads as unfinished.
    addParapet(b, building, tint)

    const clutterCount = Math.floor(building.variant * 3.99)
    for (let i = 0; i < clutterCount; i++) {
      const t = (building.variant * (i + 1) * 7.3) % 1
      const u = (building.variant * (i + 3) * 3.1) % 1
      const cw = 1.6 + t * 3
      const cd = 1.6 + u * 3
      const ch = 1.2 + t * 2.6
      const cx = x + (u - 0.5) * Math.max(0, building.width - cw - 3)
      const cz = z + (t - 0.5) * Math.max(0, building.depth - cd - 3)
      addBox(b, cx, h, cz, cw, ch, cd, tint)
    }
  }

  return toGeometry(b)
}

function addParapet(b: MeshBuilder, building: Building, tint: [number, number, number]): void {
  const hw = building.width / 2
  const hd = building.depth / 2
  const h = building.height
  const { x, z } = building
  const t = 0.35
  const ph = 1.0
  addBox(b, x, h, z + hd - t / 2, building.width, ph, t, tint)
  addBox(b, x, h, z - hd + t / 2, building.width, ph, t, tint)
  addBox(b, x + hw - t / 2, h, z, t, ph, building.depth - t * 2, tint)
  addBox(b, x - hw + t / 2, h, z, t, ph, building.depth - t * 2, tint)
}

/** Axis-aligned box sitting on `baseY`, centred on x/z. */
function addBox(
  b: MeshBuilder,
  x: number,
  baseY: number,
  z: number,
  width: number,
  height: number,
  depth: number,
  tint: [number, number, number],
): void {
  const hw = width / 2
  const hd = depth / 2
  const y0 = baseY
  const y1 = baseY + height
  const uw = width / 6
  const ud = depth / 6
  const uh = height / 6

  quad(b, [[x - hw, y0, z + hd], [x + hw, y0, z + hd], [x + hw, y1, z + hd], [x - hw, y1, z + hd]], [0, 0, 1], uw, uh, 0, tint)
  quad(b, [[x + hw, y0, z - hd], [x - hw, y0, z - hd], [x - hw, y1, z - hd], [x + hw, y1, z - hd]], [0, 0, -1], uw, uh, 0, tint)
  quad(b, [[x + hw, y0, z + hd], [x + hw, y0, z - hd], [x + hw, y1, z - hd], [x + hw, y1, z + hd]], [1, 0, 0], ud, uh, 0, tint)
  quad(b, [[x - hw, y0, z - hd], [x - hw, y0, z + hd], [x - hw, y1, z + hd], [x - hw, y1, z - hd]], [-1, 0, 0], ud, uh, 0, tint)
  quad(b, [[x - hw, y1, z - hd], [x + hw, y1, z - hd], [x + hw, y1, z + hd], [x - hw, y1, z + hd]], [0, 1, 0], uw, ud, 0, tint)
}

function facadeTint(variant: number): [number, number, number] {
  // Narrow band of cool greys/blues — variety without a clown-coloured skyline.
  const warm = 0.82 + variant * 0.3
  return [0.62 * warm, 0.67 * warm, 0.78 * warm]
}

function roofTint(variant: number): [number, number, number] {
  const v = 0.42 + variant * 0.22
  return [v, v * 1.02, v * 1.1]
}

// ---------------------------------------------------------------------------
// textures — generated, so there are no assets to load, cache-bust or ship
// ---------------------------------------------------------------------------

const FACADE_PX = 256
/** Windows per texture tile. Must match between albedo and emissive. */
const WINDOWS_X = 4
const WINDOWS_Y = 2
/** Fraction of windows with the lights on. */
const LIT_FRACTION = 0.24

function facadeCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = FACADE_PX
  return [canvas, canvas.getContext('2d')!]
}

/** Deterministic per-window pseudo-random, so albedo and emissive agree. */
function windowSeed(ix: number, iy: number): number {
  const s = Math.sin(ix * 127.1 + iy * 311.7) * 43758.5453
  return s - Math.floor(s)
}

/** Warm and cool office lighting, so the skyline is not uniformly sodium. */
function litColour(r: number): string {
  const pick = (r * 97) % 1
  if (pick > 0.66) return '#cfe0ff'
  if (pick > 0.33) return '#ffe6bc'
  return '#ffcf92'
}

function eachWindow(fn: (x: number, y: number, w: number, h: number, r: number) => void): void {
  const cw = FACADE_PX / WINDOWS_X
  const ch = FACADE_PX / WINDOWS_Y
  for (let ix = 0; ix < WINDOWS_X; ix++) {
    for (let iy = 0; iy < WINDOWS_Y; iy++) {
      fn(ix * cw + cw * 0.18, iy * ch + ch * 0.16, cw * 0.64, ch * 0.46, windowSeed(ix, iy))
    }
  }
}

function makeFacadeTexture(): CanvasTexture {
  const [canvas, ctx] = facadeCanvas()
  ctx.fillStyle = '#39434f'
  ctx.fillRect(0, 0, FACADE_PX, FACADE_PX)

  // Vertical piers between window bays — the main thing that reads as
  // "building" rather than "grid" once you are more than a block away.
  const cw = FACADE_PX / WINDOWS_X
  ctx.fillStyle = '#4d5765'
  for (let ix = 0; ix < WINDOWS_X; ix++) ctx.fillRect(ix * cw, 0, cw * 0.14, FACADE_PX)

  eachWindow((x, y, w, h, r) => {
    ctx.fillStyle = r > 1 - LIT_FRACTION ? '#6d6754' : '#1b232f'
    ctx.fillRect(x, y, w, h)
  })

  // Floor slab bands give the eye a vertical scale reference while falling.
  const ch = FACADE_PX / WINDOWS_Y
  ctx.fillStyle = 'rgba(72, 82, 96, 0.9)'
  for (let iy = 0; iy < WINDOWS_Y; iy++) ctx.fillRect(0, iy * ch + ch * 0.72, FACADE_PX, ch * 0.12)

  return finishTexture(canvas)
}

function makeWindowEmissiveTexture(): CanvasTexture {
  const [canvas, ctx] = facadeCanvas()
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, FACADE_PX, FACADE_PX)

  eachWindow((x, y, w, h, r) => {
    if (r <= 1 - LIT_FRACTION) return
    ctx.fillStyle = litColour(r)
    ctx.fillRect(x, y, w, h)
  })
  return finishTexture(canvas)
}

function makeRoofTexture(): CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 128
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#4a5260'
  ctx.fillRect(0, 0, 128, 128)
  // Speckle reads as gravel and, more usefully, gives the roof a texture
  // gradient so you can judge closing speed on a landing.
  for (let i = 0; i < 900; i++) {
    const v = 40 + Math.floor(windowSeed(i, i * 3) * 60)
    ctx.fillStyle = `rgb(${v},${v + 4},${v + 12})`
    const x = windowSeed(i * 2, 7) * 128
    const y = windowSeed(5, i * 2) * 128
    ctx.fillRect(x, y, 2, 2)
  }
  return finishTexture(canvas)
}

function finishTexture(canvas: HTMLCanvasElement): CanvasTexture {
  const texture = new CanvasTexture(canvas)
  texture.wrapS = texture.wrapT = RepeatWrapping
  texture.anisotropy = 8
  texture.colorSpace = SRGBColorSpace
  return texture
}
